import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { EmailCheckForm } from '../components/EmailCheckForm';
import { LoginForm } from '../components/LoginForm';
import { RegisterForm } from '../components/RegisterForm';
import { useApp } from '../context/AppContext';
import { authService } from '../services/authService';

type AuthStep = 'email-check' | 'login' | 'register';

export const AuthPage: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<AuthStep>('email-check');
  const [email, setEmail] = useState('');
  const navigate = useNavigate();
  const { state, dispatch } = useApp();

  // Check if user is already authenticated
  useEffect(() => {
    const checkAuth = async () => {
      if (authService.isAuthenticated() && state.user) {
        const mfaOk = await authService.validateAdminMfaAccess();
        if (!mfaOk) {
          authService.removeToken();
          dispatch({ type: 'LOGOUT' });
          return;
        }
        navigate('/admin');
        return;
      }

      // Try to get user profile if token exists
      if (authService.isAuthenticated()) {
        try {
          const response = await authService.getProfile();
          // Check if user has admin role
          if (!authService.isAdmin(response.data)) {
            authService.removeToken();
            navigate('/admin/access-denied');
            return;
          }
          const mfaOk = await authService.validateAdminMfaAccess();
          if (!mfaOk) {
            authService.removeToken();
            dispatch({ type: 'LOGOUT' });
            return;
          }
          dispatch({
            type: 'SET_USER',
            payload: response.data,
          });
          navigate('/admin');
        } catch (error) {
          // Token is invalid, remove it
          authService.removeToken();
          dispatch({ type: 'LOGOUT' });
        }
      }
    };

    checkAuth();
  }, [navigate, dispatch, state.user]);

  const handleEmailExists = (userEmail: string) => {
    setEmail(userEmail);
    setCurrentStep('login');
  };

  const handleEmailNew = (userEmail: string) => {
    setEmail(userEmail);
    setCurrentStep('register');
  };

  const handleBackToEmailCheck = () => {
    setCurrentStep('email-check');
    setEmail('');
  };

  const handleAuthSuccess = () => {
    navigate('/admin');
  };

  // Don't render anything while checking authentication
  if (authService.isAuthenticated() && state.user) {
    return null;
  }

  return (
    <div style={{ position: 'relative' }}>
      {/* Email Check Form - always rendered but conditionally visible */}
      <div style={{ 
        display: currentStep === 'email-check' ? 'block' : 'none',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: currentStep === 'email-check' ? 1 : -1
      }}>
        <EmailCheckForm
          onEmailExists={handleEmailExists}
          onEmailNew={handleEmailNew}
        />
      </div>

      {/* Login Form - always rendered but conditionally visible */}
      <div style={{ 
        display: currentStep === 'login' ? 'block' : 'none',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: currentStep === 'login' ? 1 : -1
      }}>
        <LoginForm
          email={email}
          onBack={handleBackToEmailCheck}
          onSuccess={handleAuthSuccess}
        />
      </div>

      {/* Register Form - always rendered but conditionally visible */}
      <div style={{ 
        display: currentStep === 'register' ? 'block' : 'none',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: currentStep === 'register' ? 1 : -1
      }}>
        <RegisterForm
          email={email}
          onBack={handleBackToEmailCheck}
          onSuccess={handleAuthSuccess}
        />
      </div>
    </div>
  );
};
